import React, { useState } from "react";
import { cashHandover, cashReturn } from "../../../../services/karkhanaApi";
import { useKT } from "../../i18n/kt";
import { rupees } from "../../rates/rateModel";
import { newRequestId } from "../../rounds/entryQueue";
import { num, refusalText } from "../money";
import Keypad from "../ui/Keypad";

export type HandKind = "HANDOVER" | "RETURN";

type Props = { supervisorUserId: string; kind: HandKind; onDone: () => void; onCancel: () => void };

/**
 * The owner gives a supervisor cash (the float top-up, the only way cash comes in) or records cash a
 * supervisor gave back, from the cash book (V1_SPEC 7.6; POST /cash-book/handover or /return, which go
 * through the money writer when the add-on is on). One request id per action, so a retry after a lost
 * answer never saves twice; a new amount or note is a new action.
 */
const HandOverCash = ({ supervisorUserId, kind, onDone, onCancel }: Props) => {
  const { t } = useKT();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [requestId, setRequestId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const edit = (set: (v: string) => void) => (v: string) => {
    setRequestId(newRequestId());
    set(v);
  };

  const save = async () => {
    if (num(amount) <= 0) return;
    setBusy(true);
    setError(null);
    try {
      const body = { amount, note: note.trim() || undefined, clientRequestId: requestId };
      const res: any = kind === "HANDOVER" ? await cashHandover(supervisorUserId, body) : await cashReturn(supervisorUserId, body);
      if (!res?.success) {
        setError(refusalText(res, t));
        return;
      }
      onDone();
    } catch {
      setError(t("No network. Tap Save again; it won't be saved twice."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cb-card">
      <h3 className="cb-section">{t(kind === "HANDOVER" ? "Hand over cash" : "Cash given back")}</h3>
      <Keypad value={amount} onChange={edit(setAmount)} label={t("Amount")} id={`cb-hand-${kind}`} />
      <label className="cb-field">
        <span className="cb-field__label">{t("Note (optional)")}</span>
        <input value={note} maxLength={200} onChange={(e) => edit(setNote)(e.target.value)} />
      </label>
      {error ? (
        <p className="cbo-row__err" role="alert">
          {error}
        </p>
      ) : null}
      <div className="cb-row-btns">
        <button type="button" className="cb-btn is-primary" disabled={busy || num(amount) <= 0} onClick={save}>
          {busy ? t("Saving…") : t("Save {{amount}}", { amount: rupees(amount || 0) })}
        </button>
        <button type="button" className="cb-btn" disabled={busy} onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
};

export default HandOverCash;
